// src/pages/NotFoundPage.js

import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import Button from '../components/common/Button';

const NotFoundSection = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 180px);
  padding: 2rem;
`;

const Container = styled.div`
  max-width: 800px;
  text-align: center;
`;

const ErrorCode = styled(motion.h1)`
  font-size: 10rem;
  font-weight: 700;
  margin: 0;
  line-height: 1;
  background: ${props => `linear-gradient(to right, ${props.theme.primary}, ${props.theme.accent})`};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  
  @media (max-width: 768px) {
    font-size: 7rem;
  }
`;

const ErrorTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin: 1.5rem 0;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const ErrorDescription = styled(motion.p)`
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  color: ${props => props.theme.text};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ButtonsContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
  }
`;

const IllustrationContainer = styled(motion.div)`
  margin-bottom: 2rem;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const NotFoundPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };
  
  return (
    <NotFoundSection>
      <Container as={motion.div} variants={containerVariants} initial="hidden" animate="visible">
        <IllustrationContainer variants={itemVariants}>
          <svg width="100%" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 180C145.228 180 182 143.228 182 98C182 52.7715 145.228 16 100 16C54.7715 16 18 52.7715 18 98C18 143.228 54.7715 180 100 180Z" fill={`${theme.primary}10`} />
            <path d="M110 130H90C84.477 130 80 125.523 80 120V80C80 74.477 84.477 70 90 70H110C115.523 70 120 74.477 120 80V120C120 125.523 115.523 130 110 130Z" stroke={theme.primary} strokeWidth="4" />
            <path d="M70 50L130 150" stroke={theme.primary} strokeWidth="4" strokeLinecap="round" />
            <path d="M130 50L70 150" stroke={theme.primary} strokeWidth="4" strokeLinecap="round" />
            <circle cx="70" cy="50" r="10" fill={theme.primary} />
            <circle cx="130" cy="50" r="10" fill={theme.primary} />
            <circle cx="70" cy="150" r="10" fill={theme.primary} />
            <circle cx="130" cy="150" r="10" fill={theme.primary} />
          </svg>
        </IllustrationContainer>
        
        <ErrorCode theme={theme} variants={itemVariants}>404</ErrorCode>
        <ErrorTitle theme={theme} variants={itemVariants}>{t('pageNotFound')}</ErrorTitle>
        <ErrorDescription theme={theme} variants={itemVariants}>
          {t('pageNotFoundDescription')}
        </ErrorDescription>
        
        <ButtonsContainer variants={itemVariants}>
          <Button
            variant="primary"
            size="large"
            as={Link}
            to="/"
          >
            <i className="fas fa-home" style={{ marginRight: '8px' }}></i>
            {t('backToHome')}
          </Button>
          
          <Button
            variant="outline"
            size="large"
            as={Link}
            to="/contact"
          >
            <i className="fas fa-envelope" style={{ marginRight: '8px' }}></i>
            {t('contactUs')}
          </Button>
        </ButtonsContainer>
      </Container>
    </NotFoundSection>
  );
};

export default NotFoundPage;